import { useState, useRef } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  styled,
  Tabs,
  Tab,
} from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import DownloadIcon from "@mui/icons-material/Download";
import CloseIcon from "@mui/icons-material/Close";
import ExcelJS from "exceljs";
import { uploadCsv } from "../axios/axiosFunctions";
import { roleLabel } from "../utils/timFamily";

const VisuallyHiddenInput = styled("input")({
  clip: "rect(0 0 0 0)",
  clipPath: "inset(50%)",
  height: 1,
  overflow: "hidden",
  position: "absolute",
  bottom: 0,
  left: 0,
  whiteSpace: "nowrap",
  width: 1,
});

const ROLE_LABELS = {
  member: "Member",
  senior: "Senior",
  both: "Senior + Member",
};

const ROLE_LABEL_TO_VALUE = {
  member: "member",
  senior: "senior",
  "senior leader": "senior",
  "team member": "member",
  both: "both",
  "senior + member": "both",
  "senior+member": "both",
  "member + senior": "both",
  "senior and team member": "both",
  // Executive Team Assessment role names map to the same stored values, so
  // the upload accepts either the TIM wording or the Executive wording.
  "executive team member": "member",
  "et member": "member",
  "executive team feedback provider": "senior",
  "et feedback provider": "senior",
  "feedback provider": "senior",
  "executive team member + feedback provider": "both",
  "member + feedback provider": "both",
};

const TEMPLATE_BLANK_ROWS = 20;

function TabPanel(props) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`tim-tabpanel-${index}`}
      aria-labelledby={`tim-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
    </div>
  );
}

const CsvUploadTIM = ({ open, onClose, groupId, groupName, onSuccess, category }) => {
  const isExecutive = category === "executive";
  // Role wording shown in the template, dropdown and instructions. The upload
  // parser accepts both the TIM and Executive wording regardless.
  const cfg = isExecutive
    ? {
        assessmentName: "Executive Team Assessment",
        worksheet: "ELT Members",
        fileName: "executive_team_assessment_template.xlsx",
        roleList: "Executive Team Member,Executive Team Feedback Provider",
        sampleRoles: ["Executive Team Feedback Provider", "Executive Team Member"],
        roleHint: "Executive Team Member or Executive Team Feedback Provider",
        roleError: "Executive Team Member or Executive Team Feedback Provider",
      }
    : {
        assessmentName: "Teams in Motion®",
        worksheet: "TIM Members",
        fileName: "tim_assessment_template.xlsx",
        roleList: "member,senior,both",
        sampleRoles: ["senior", "member", "both"],
        roleHint: "member, senior, or both",
        roleError: "member, senior, or both",
      };
  const fileInputRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [parsedMembers, setParsedMembers] = useState([]);
  const [errors, setErrors] = useState([]);
  const [showPreview, setShowPreview] = useState(false);
  const [alertMsg, setAlertMsg] = useState("");
  const [alertSeverity, setAlertSeverity] = useState("error");
  const [showAlert, setShowAlert] = useState(false);
  const [tabValue, setTabValue] = useState(0);
  const [invalidRows, setInvalidRows] = useState([]);

  const downloadTemplate = async () => {
    const workbook = new ExcelJS.Workbook();
    workbook.creator = "K2G Assessments";
    workbook.created = new Date();
    const worksheet = workbook.addWorksheet(cfg.worksheet);

    worksheet.columns = [
      { header: "firstName", key: "firstName", width: 20 },
      { header: "lastName", key: "lastName", width: 20 },
      { header: "email", key: "email", width: 32 },
      { header: "role", key: "role", width: 14 },
    ];

    worksheet.getRow(1).fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: "FF0F5385" },
    };
    worksheet.getRow(1).font = { bold: true, color: { argb: "FFFFFFFF" } };

    const sampleRows = [
      { firstName: "Jane", lastName: "Smith", email: "jane.smith@example.com" },
      { firstName: "John", lastName: "Doe", email: "john.doe@example.com" },
      { firstName: "Alice", lastName: "Brown", email: "alice.brown@example.com" },
    ];
    cfg.sampleRoles.forEach((role, i) => {
      worksheet.addRow({ ...sampleRows[i % sampleRows.length], role });
    });

    for (let i = 0; i < TEMPLATE_BLANK_ROWS; i++) {
      worksheet.addRow({});
    }

    const lastRow = worksheet.rowCount;
    for (let r = 2; r <= lastRow; r++) {
      worksheet.getCell(`D${r}`).dataValidation = {
        type: "list",
        allowBlank: true,
        formulae: [`"${cfg.roleList}"`],
        showErrorMessage: true,
        errorStyle: "error",
        errorTitle: "Invalid role",
        error: `Role must be: ${cfg.roleError}`,
      };
    }

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", cfg.fileName);
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const normalizeRole = (value) => {
    if (!value) return "member";
    const normalized = String(value).toLowerCase().trim();
    return ROLE_LABEL_TO_VALUE[normalized] || null;
  };

  const cellText = (cell) => {
    if (cell == null) return "";
    const v = cell.value;
    if (v == null) return "";
    if (typeof v === "object") {
      if (v.text) return String(v.text).trim();
      if (v.result) return String(v.result).trim();
      if (Array.isArray(v.richText)) {
        return v.richText.map((rt) => rt.text || "").join("").trim();
      }
      return "";
    }
    return String(v).trim();
  };

  const parseXlsx = async (file) => {
    const buffer = await file.arrayBuffer();
    const workbook = new ExcelJS.Workbook();
    await workbook.xlsx.load(buffer);
    const worksheet = workbook.worksheets[0];
    if (!worksheet) {
      return {
        members: [],
        errors: ["Workbook has no worksheets"],
        invalidRows: [],
      };
    }

    const members = [];
    const parseErrors = [];
    const invalidRows = [];
    const seenEmails = new Set();

    worksheet.eachRow((row, rowNumber) => {
      if (rowNumber === 1) return;

      const firstName = cellText(row.getCell(1));
      const lastName = cellText(row.getCell(2));
      const email = cellText(row.getCell(3));
      const roleValue = cellText(row.getCell(4));

      if (!firstName && !lastName && !email && !roleValue) {
        return;
      }

      const rowData = {
        line: rowNumber,
        data: { firstName, lastName, email, roleValue },
        errors: [],
      };

      if (!firstName) rowData.errors.push("First name is required");
      if (!email) {
        rowData.errors.push("Email is required");
      } else if (!validateEmail(email)) {
        rowData.errors.push("Invalid email format");
      } else {
        const emailLower = email.toLowerCase();
        if (seenEmails.has(emailLower)) {
          rowData.errors.push(`Duplicate email: ${email}`);
        }
      }

      const normalizedRole = normalizeRole(roleValue);
      if (roleValue && !normalizedRole) {
        rowData.errors.push(
          `Invalid role "${roleValue}". Must be: ${cfg.roleError}`,
        );
      }

      if (rowData.errors.length > 0) {
        invalidRows.push(rowData);
        parseErrors.push(`Row ${rowNumber}: ${rowData.errors.join("; ")}`);
        return;
      }

      const emailLower = email.toLowerCase();
      seenEmails.add(emailLower);
      members.push({
        firstName,
        lastName,
        email: emailLower,
        role: normalizedRole,
      });
    });

    return { members, errors: parseErrors, invalidRows };
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!/\.xlsx?$/i.test(file.name)) {
      setAlertMsg("Please select an .xlsx file");
      setAlertSeverity("error");
      setShowAlert(true);
      setTimeout(() => setShowAlert(false), 3000);
      return;
    }

    try {
      const {
        members,
        errors: parseErrors,
        invalidRows: invalidRowsData,
      } = await parseXlsx(file);

      setParsedMembers(members);
      setErrors(parseErrors);
      setInvalidRows(invalidRowsData);
      setShowPreview(true);
      setTabValue(0);
    } catch (error) {
      console.error("xlsx parse error:", error);
      setAlertMsg("Error reading .xlsx file");
      setAlertSeverity("error");
      setShowAlert(true);
      setTimeout(() => setShowAlert(false), 3000);
    }

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const buildCleanCsvBlob = (members) => {
    const headers = ["firstName", "lastName", "email", "role"];
    const rows = members.map((m) =>
      [m.firstName, m.lastName, m.email, m.role].join(","),
    );
    const csvContent = [headers.join(","), ...rows].join("\n");
    return new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  };

  const handleUpload = async () => {
    if (parsedMembers.length === 0) {
      setAlertMsg("No valid members to upload");
      setAlertSeverity("error");
      setShowAlert(true);
      setTimeout(() => setShowAlert(false), 3000);
      return;
    }

    setLoading(true);
    try {
      const blob = buildCleanCsvBlob(parsedMembers);
      const formData = new FormData();
      formData.append("file", blob, "tim_members.csv");
      formData.append("groupId", groupId);

      const result = await uploadCsv(formData);

      if (result && result.msg === "ok") {
        setLoading(false);
        setAlertMsg(`Successfully added ${parsedMembers.length} member(s)`);
        setAlertSeverity("success");
        setShowAlert(true);
        setTimeout(() => {
          setShowAlert(false);
          handleClose();
          if (onSuccess) onSuccess();
        }, 2000);
      } else {
        setLoading(false);
        setAlertMsg("Failed to upload data");
        setAlertSeverity("error");
        setShowAlert(true);
        setTimeout(() => setShowAlert(false), 5000);
      }
    } catch (error) {
      setLoading(false);
      setAlertMsg("Error uploading data");
      setAlertSeverity("error");
      setShowAlert(true);
      setTimeout(() => setShowAlert(false), 3000);
    }
  };

  const handleClose = () => {
    setParsedMembers([]);
    setErrors([]);
    setInvalidRows([]);
    setShowPreview(false);
    setShowAlert(false);
    setTabValue(0);
    onClose();
  };

  const hasValidData = parsedMembers.length > 0;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h5">
            Upload {cfg.assessmentName} Members (.xlsx)
            {groupName ? ` — ${groupName}` : ""}
          </Typography>
          <IconButton onClick={handleClose}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ mb: 3 }}>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Upload an .xlsx file containing the team members for this{" "}
            {cfg.assessmentName} assessment. The role column is a dropdown.
          </Typography>

          <Box display="flex" gap={2} sx={{ mb: 2 }} flexWrap="wrap">
            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={downloadTemplate}
            >
              Download Sample Template
            </Button>
            <Button
              variant="contained"
              component="label"
              startIcon={<CloudUploadIcon />}
            >
              Select .xlsx File
              <VisuallyHiddenInput
                type="file"
                accept=".xlsx"
                onChange={handleFileSelect}
                ref={fileInputRef}
              />
            </Button>
          </Box>

          <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
            <strong>Format:</strong>
          </Typography>
          <Typography variant="caption" color="textSecondary" component="div">
            Required columns: <strong>firstName</strong>, <strong>lastName</strong>,{" "}
            <strong>email</strong>, <strong>role</strong>
          </Typography>
          {isExecutive ? (
            <Typography variant="caption" color="textSecondary" component="div">
              <strong>role</strong> must be one of:{" "}
              <code>Executive Team Member</code> or{" "}
              <code>Executive Team Feedback Provider</code>. Feedback Providers
              evaluate the leadership team; everyone else is an Executive Team
              Member. (The short forms <code>ET Member</code> and{" "}
              <code>ET Feedback Provider</code> are also accepted.)
            </Typography>
          ) : (
            <Typography variant="caption" color="textSecondary" component="div">
              <strong>role</strong> must be one of: <code>senior</code>,{" "}
              <code>member</code>, or <code>both</code>. Each team will have several{" "}
              <strong>senior leaders</strong> (marked as <code>senior</code>) who
              evaluate the team using the Senior Leader assessment. Everyone else
              is marked as <code>member</code>. Use <code>both</code> for a senior
              leader who is also a team member — they take the member assessment
              plus the senior block at the end.
            </Typography>
          )}
          <Typography variant="caption" color="textSecondary" component="div">
            Email addresses must be unique within the file.
          </Typography>
        </Box>

        <Divider sx={{ my: 2 }} />

        {errors.length > 0 && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            <Typography variant="subtitle2">Validation Warnings:</Typography>
            <ul style={{ margin: 0, paddingLeft: 20 }}>
              {errors.slice(0, 10).map((err, i) => (
                <li key={i}>{err}</li>
              ))}
              {errors.length > 10 && (
                <li>...and {errors.length - 10} more errors</li>
              )}
            </ul>
          </Alert>
        )}

        {showPreview && (hasValidData || invalidRows.length > 0) && (
          <>
            <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}>
              <Tabs
                value={tabValue}
                onChange={(e, newValue) => setTabValue(newValue)}
                aria-label="preview tabs"
              >
                <Tab
                  label={`Members (${parsedMembers.length})`}
                  id="tim-tab-0"
                />
                {invalidRows.length > 0 && (
                  <Tab
                    label={`Invalid Rows (${invalidRows.length})`}
                    id="tim-tab-1"
                    sx={{ color: "error.main" }}
                  />
                )}
              </Tabs>
            </Box>

            <TabPanel value={tabValue} index={0}>
              {parsedMembers.length > 0 ? (
                <TableContainer component={Paper} sx={{ maxHeight: 300 }}>
                  <Table stickyHeader size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell><strong>First Name</strong></TableCell>
                        <TableCell><strong>Last Name</strong></TableCell>
                        <TableCell><strong>Email</strong></TableCell>
                        <TableCell><strong>Role</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {parsedMembers.map((user, idx) => (
                        <TableRow key={idx}>
                          <TableCell>{user.firstName}</TableCell>
                          <TableCell>{user.lastName}</TableCell>
                          <TableCell>{user.email}</TableCell>
                          <TableCell>
                            {roleLabel(user.role, category) || ROLE_LABELS[user.role] || user.role}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Typography color="textSecondary">
                  No members to display
                </Typography>
              )}
            </TabPanel>

            <TabPanel value={tabValue} index={1}>
              {invalidRows.length > 0 && (
                <Box>
                  <Alert severity="error" sx={{ mb: 2 }}>
                    <Typography variant="subtitle2">
                      {invalidRows.length} row(s) with errors:
                    </Typography>
                  </Alert>
                  <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
                    <Table stickyHeader size="small">
                      <TableHead>
                        <TableRow sx={{ backgroundColor: "#ffebee" }}>
                          <TableCell><strong>Row</strong></TableCell>
                          <TableCell><strong>Email</strong></TableCell>
                          <TableCell><strong>Error(s)</strong></TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {invalidRows.map((row, idx) => (
                          <TableRow
                            key={idx}
                            sx={{ backgroundColor: "#fff3e0" }}
                          >
                            <TableCell>{row.line}</TableCell>
                            <TableCell>
                              <Typography variant="caption">
                                {row.data?.email || "—"}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Box>
                                {row.errors.map((error, errIdx) => (
                                  <Typography
                                    key={errIdx}
                                    variant="caption"
                                    display="block"
                                    color="error"
                                    sx={{ mb: 0.5 }}
                                  >
                                    • {error}
                                  </Typography>
                                ))}
                              </Box>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              )}
            </TabPanel>
          </>
        )}

        {showAlert && (
          <Alert severity={alertSeverity} sx={{ mt: 2 }}>
            {alertMsg}
          </Alert>
        )}

        <Box display="flex" justifyContent="center" gap={2} sx={{ mt: 3 }}>
          {hasValidData && (
            <Button
              variant="contained"
              onClick={handleUpload}
              disabled={loading || parsedMembers.length === 0}
              startIcon={loading ? <CircularProgress size={20} /> : null}
            >
              {loading ? "Uploading..." : "Upload Members"}
            </Button>
          )}
          <Button variant="outlined" onClick={handleClose} color="inherit">
            Cancel
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default CsvUploadTIM;
